/**
 * FB65 — the job page is the WHOLE FILE.
 *
 * `26-1273` is one restoration file that may hold several `jobs` rows —
 * `26-1273-WTR`, `26-1273-MLD`, `26-1273-REC`. Only the MAIN job (the earliest
 * of them, i.e. the original intake) has a page. Ask for a sibling's URL and
 * you are redirected to the main job with that division's Job Info section
 * already selected; the sibling's data is reached through its sections and
 * nowhere else.
 *
 * Two mechanics make that hold up:
 *
 *   • The redirect happens in `beforeLoad`, so it covers EVERY link in the app
 *     — dashboards, search, Ninety tasks, the Gantt — without any of them
 *     having to know about families. The family is read through
 *     `queryClient.ensureQueryData(familyQueryOptions(base))`, the same cache
 *     entry the tab row then uses, so it costs one request, not two.
 *   • The selected section lives in `?tab=`, not in component state, so a
 *     redirect can name it, a refresh keeps it, and the URL can be pasted to
 *     somebody else and land on the same panel.
 *
 * A solo job never notices any of this: `familyRedirect` returns null, the tab
 * row has no sibling groups, and the page is exactly what it was.
 */
import { useCallback } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useJob } from '~/hooks/useJob';
import { JobHeader } from '~/components/job/JobHeader';
import { JobTopTabs } from '~/components/job/JobTopTabs';
import { TeamRow } from '~/components/job/TeamRow';
import { JobBottomTabs } from '~/components/job/JobBottomTabs';
import { familyQueryOptions } from '~/components/job/use-job-family';
import {
  familyBaseOf, familyRedirect, familySectionTab, hasFamilyBase,
} from '~/components/job/job-family';

export interface JobPageSearch {
  /**
   * The selected top tab: a primary key (`customer` · `job` · `dates` ·
   * `insurance` · `accounting` · `billing`) or a division section
   * (`job-info:26-1273-MLD` / `accounting:26-1273-MLD`). Absent = Customer Info,
   * so the plain job URL stays clean.
   */
  tab?: string;
}

/**
 * Where this URL really belongs. Returns null — meaning "render here" — for the
 * main job, for a solo job, for an off-format number, and for any failure:
 * a network blip must never strand somebody on a blank page instead of the job
 * they asked for.
 */
async function resolveFamilyRedirect(
  queryClient: QueryClient,
  jobNumber: string,
  tab: string | undefined,
) {
  if (!hasFamilyBase(jobNumber)) return null;
  try {
    const members = await queryClient.ensureQueryData(
      familyQueryOptions(familyBaseOf(jobNumber)),
    );
    return familyRedirect({ jobNumber, members, tab });
  } catch {
    return null;
  }
}

export const Route = createFileRoute('/_app/jobs/$jobNumber')({
  validateSearch: (search: Record<string, unknown>): JobPageSearch => {
    const tab = typeof search.tab === 'string' ? search.tab.trim() : '';
    return tab ? { tab } : {};
  },
  beforeLoad: async ({ context, params, search }) => {
    const target = await resolveFamilyRedirect(context.queryClient, params.jobNumber, search.tab);
    if (target) {
      throw redirect({
        to: '/jobs/$jobNumber',
        params: { jobNumber: target.jobNumber },
        search: { tab: target.tab },
        // The sibling URL is a doorway, not a destination — it should not sit
        // in the history stack for Back to bounce off.
        replace: true,
      });
    }
  },
  component: JobPage,
});

function JobPage() {
  const { jobNumber } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { data, isLoading, error } = useJob(jobNumber);

  // Selecting a tab rewrites `?tab=`; Customer Info clears it so the ordinary
  // job URL has no query string. `replace` keeps Back meaning "the page before
  // this job" rather than "the tab before this tab".
  const selectTab = useCallback((key: string) => {
    navigate({
      to: '/jobs/$jobNumber',
      params: { jobNumber },
      search: key && key !== 'customer' ? { tab: key } : {},
      replace: true,
    });
  }, [navigate, jobNumber]);

  if (isLoading) {
    return <div className="px-6 py-12 text-sm text-[var(--color-ink-muted)]">Loading job {jobNumber}…</div>;
  }
  if (error || !data) {
    return (
      <div className="px-6 py-12 max-w-2xl mx-auto">
        <h1 className="font-display text-2xl font-bold text-[var(--color-danger)]">Couldn't load job {jobNumber}</h1>
        <p className="text-sm text-[var(--color-ink-soft)] mt-2">{error?.message ?? 'Unknown error.'}</p>
      </div>
    );
  }

  return (
    <div className="pb-12">
      {/* FB65 — the header's sibling division buttons select that division's
          section on THIS page instead of navigating to a page of its own. */}
      <JobHeader
        bundle={data}
        onOpenDivision={(sibling) => selectTab(familySectionTab('job', sibling))}
      />
      <div className="px-6 py-6 space-y-6">
        {/* FB6 — one full-width click-through tab row replaces the stacked
            Customer / Job Info / Dates / Accounting / Insurance cards. */}
        <JobTopTabs bundle={data} activeKey={tab} onSelect={selectTab} />

        {/* The six-person team, one color-coded row. */}
        <TeamRow bundle={data} />

        <JobBottomTabs
          jobId={data.job.id!}
          jobNumber={data.job.job_number!}
          division={data.job.division ?? null}
          customerId={data.job.customer_id ?? null}
          customerName={data.job.customer_name ?? null}
        />
      </div>
    </div>
  );
}
